import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import CardItem from '@/app/pages/marketplace/components/CardItem'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import { Input } from '@/common/components/Input.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SimpleSelector, {
  ListItem,
} from '@/common/components/SimpleSelector.tsx'
import { TableToolBar } from '@/common/components/StackedTable.tsx'
import { BackIcon, FilterIcon, MagnifierIcon } from '@/common/icons/general.tsx'
import { useTranslation } from 'react-i18next'

type AffiliationItemsProps = {
  items: MarketplaceItem[]
  totalRecords: number
  search: string
  filters: ListItem[]
  currentPage: number
  perPage: number
  onSearchChange: (value: string) => void
  onFilterChange: (item: ListItem) => void
  onItemClick: (item: MarketplaceItem) => void
  onBack: () => void
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
}

export default function AffiliationItems({
  items,
  totalRecords,
  search,
  filters,
  currentPage,
  perPage,
  onSearchChange,
  onFilterChange,
  onItemClick,
  onBack,
  onPageChange,
  onPerPageChange,
}: AffiliationItemsProps) {
  const { t } = useTranslation()

  return (
    <div>
      <div className="ml-2 text-left p-4 rounded-md text-md font-semibold mb-8 bg-gray-50">
        <div className="flex gap-x-4 items-center">
          <button
            type="button"
            onClick={onBack}
            aria-label={t('go_back')}
            title={t('go_back')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <BackIcon />
          </button>

          <div>{t('affiliation_items')}</div>
        </div>
      </div>

      <Card className="overflow-visible">
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="flex gap-2">
            <div>
              {items.length} {items.length === 1 ? t('item') : t('items')}
            </div>
          </div>

          <TableToolBar>
            <div className="relative rounded-md shadow-sm w-full">
              <Input
                leftIcon={<MagnifierIcon />}
                className="shadow-none"
                placeholder={t('search_by_name_or_type')}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
            <SimpleSelector
              list={filters}
              onChange={onFilterChange}
              className="pt-4 lg-sm:ps-4 lg-sm:pt-0"
              icon={<FilterIcon className="text-primary" />}
            />
          </TableToolBar>
        </CardHeader>

        <CardContent>
          <div className="flex flex-col">
            {items.length > 0 ? (
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6">
                {items.map((item) => (
                  <CardItem
                    key={item.id}
                    item={item}
                    provider={item.provider}
                    onItemClick={onItemClick}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-10">
                {t('no_affiliation_items_found')}
              </div>
            )}
          </div>
        </CardContent>

        <CardFooter className="p-0 border-t border-transparent mt-5">
          <Pagination
            itemsPerPage={perPage}
            currentPage={currentPage}
            totalRows={totalRecords}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </CardFooter>
      </Card>
    </div>
  )
}
